/* eslint-disable no-bitwise, no-continue, default-case */
// This module is a low-level raster/EXIF byte parser; bitwise ops, `continue`,
// and switch fall-through are inherent to that and intentionally allowed here.
export interface RasterUploadImageMetadata {
  XResolution: string;
  YResolution: string;
  ResolutionUnit?: string;
}

export interface RasterUploadMetadata {
  width: number;
  height: number;
  bytes: number;
  imageMetadata?: RasterUploadImageMetadata | undefined;
}

const PNG_SIGNATURE = new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10]);
const EXIF_HEADER_BYTES = 64 * 1024;
// Keep in sync with ImageDatumCreator::MIN_BELIEVABLE_RESOLUTION_PPI.
const MIN_BELIEVABLE_RESOLUTION_PPI = 10;

const readUint16Le = (bytes: Uint8Array, offset: number): number =>
  (bytes[offset] ?? 0) | ((bytes[offset + 1] ?? 0) << 8);

const readUint32Le = (bytes: Uint8Array, offset: number): number =>
  (bytes[offset] ?? 0) |
  ((bytes[offset + 1] ?? 0) << 8) |
  ((bytes[offset + 2] ?? 0) << 16) |
  ((bytes[offset + 3] ?? 0) << 24);

const readAscii = (bytes: Uint8Array, offset: number, length: number): string =>
  String.fromCharCode(...bytes.subarray(offset, offset + length));

const detectLocalRasterImageFormat = (
  mimeType: string,
  bytes: Uint8Array
): "jpeg" | "png" | "webp" | "bmp" | null => {
  const mime = mimeType.toLowerCase();

  if (/^image\/(jpeg|pjpeg)$/.test(mime)) {
    return "jpeg";
  }

  if (/^image\/png$/.test(mime)) {
    return "png";
  }

  if (/^image\/webp$/.test(mime)) {
    return "webp";
  }

  if (/^image\/(bmp|x-ms-bmp)$/.test(mime)) {
    return "bmp";
  }

  if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xd8) {
    return "jpeg";
  }

  if (
    bytes.length >= 8 &&
    PNG_SIGNATURE.every((value, index) => bytes[index] === value)
  ) {
    return "png";
  }

  if (
    bytes.length >= 12 &&
    readAscii(bytes, 0, 4) === "RIFF" &&
    readAscii(bytes, 8, 4) === "WEBP"
  ) {
    return "webp";
  }

  if (bytes.length >= 2 && bytes[0] === 0x42 && bytes[1] === 0x4d) {
    return "bmp";
  }

  return null;
};

export const readPixelDimensions = async (
  file: File
): Promise<{
  width: number;
  height: number;
}> => {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(file, {
        imageOrientation: "from-image",
      });
      const { width, height } = bitmap;

      bitmap.close();

      if (width > 0 && height > 0) {
        return { width, height };
      }
    } catch {
      // fall through to Image element
    }
  }

  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve({
        width: image.naturalWidth,
        height: image.naturalHeight,
      });
    };

    image.onerror = () => {
      URL.revokeObjectURL(url);
      // Browsers can't decode some allowed upload formats (TIFF, PSD, HEIC/HEIF),
      // so neither createImageBitmap nor <img> can measure them. Don't abort the
      // upload — resolve unknown (0x0) dimensions. The server (ImageDatumCreator)
      // treats width/height that aren't between 1..MAX_TRUSTED_DIMENSION as
      // untrusted and derives the real dimensions from the file via MiniMagick.
      resolve({ width: 0, height: 0 });
    };

    image.src = url;
  });
};

const readTiffUint16 = (
  bytes: Uint8Array,
  offset: number,
  littleEndian: boolean
): number =>
  littleEndian
    ? readUint16Le(bytes, offset)
    : ((bytes[offset] ?? 0) << 8) | (bytes[offset + 1] ?? 0);

const readTiffUint32 = (
  bytes: Uint8Array,
  offset: number,
  littleEndian: boolean
): number => {
  if (littleEndian) {
    return readUint32Le(bytes, offset);
  }

  return (
    ((bytes[offset] ?? 0) << 24) |
    ((bytes[offset + 1] ?? 0) << 16) |
    ((bytes[offset + 2] ?? 0) << 8) |
    (bytes[offset + 3] ?? 0)
  );
};

const readTiffRational = (
  bytes: Uint8Array,
  offset: number,
  littleEndian: boolean
): number | null => {
  if (offset + 8 > bytes.length) {
    return null;
  }

  const numerator = readTiffUint32(bytes, offset, littleEndian);
  const denominator = readTiffUint32(bytes, offset + 4, littleEndian);

  if (denominator === 0) {
    return null;
  }

  return numerator / denominator;
};

const readIfdTagValue = (
  bytes: Uint8Array,
  tiffOffset: number,
  _tiffLimit: number,
  entryOffset: number,
  littleEndian: boolean
): number | null => {
  const tagType = readTiffUint16(bytes, entryOffset + 2, littleEndian);
  const valueCount = readTiffUint32(bytes, entryOffset + 4, littleEndian);
  const valueOffset = readTiffUint32(bytes, entryOffset + 8, littleEndian);

  if (tagType === 5 && valueCount >= 1) {
    const rationalOffset = tiffOffset + valueOffset;

    return readTiffRational(bytes, rationalOffset, littleEndian);
  }

  if (tagType === 4 && valueCount >= 1) {
    return valueOffset;
  }

  if (tagType === 3 && valueCount >= 1) {
    return readTiffUint16(bytes, entryOffset + 8, littleEndian);
  }

  return null;
};

const readJpegExifResolution = (
  bytes: Uint8Array
): RasterUploadImageMetadata | null => {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) {
    return null;
  }

  let offset = 2;

  while (offset + 1 < bytes.length) {
    if (bytes[offset] !== 0xff) {
      return null;
    }

    const marker = bytes[offset + 1];

    if (marker === 0xda) {
      return null;
    }

    if (offset + 3 >= bytes.length) {
      return null;
    }

    const segmentLength =
      ((bytes[offset + 2] ?? 0) << 8) | (bytes[offset + 3] ?? 0);
    const segmentEnd = offset + 2 + segmentLength;

    if (segmentEnd > bytes.length) {
      return null;
    }

    if (
      marker === 0xe1 &&
      segmentEnd - offset >= 10 &&
      readAscii(bytes, offset + 4, 4) === "Exif"
    ) {
      const tiffOffset = offset + 10;
      const tiffLimit = segmentEnd;

      if (tiffLimit < tiffOffset + 8) {
        return null;
      }

      let littleEndian = false;

      if (bytes[tiffOffset] === 0x49 && bytes[tiffOffset + 1] === 0x49) {
        littleEndian = true;
      } else if (bytes[tiffOffset] === 0x4d && bytes[tiffOffset + 1] === 0x4d) {
        littleEndian = false;
      } else {
        return null;
      }

      if (readTiffUint16(bytes, tiffOffset + 2, littleEndian) !== 0x002a) {
        return null;
      }

      const ifd0Offset = readTiffUint32(bytes, tiffOffset + 4, littleEndian);
      const ifd0Start = tiffOffset + ifd0Offset;

      if (ifd0Start + 2 > tiffLimit) {
        return null;
      }

      const entryCount = readTiffUint16(bytes, ifd0Start, littleEndian);
      const entriesStart = ifd0Start + 2;
      let xResolution: number | null = null;
      let yResolution: number | null = null;
      let resolutionUnit: number | null = null;

      for (let index = 0; index < entryCount; index += 1) {
        const entryOffset = entriesStart + index * 12;

        if (entryOffset + 12 > tiffLimit) {
          break;
        }

        const tag = readTiffUint16(bytes, entryOffset, littleEndian);
        const value = readIfdTagValue(
          bytes,
          tiffOffset,
          tiffLimit,
          entryOffset,
          littleEndian
        );

        if (value === null) {
          continue;
        }

        switch (tag) {
          case 0x011a: {
            xResolution = value;

            break;
          }
          case 0x011b: {
            yResolution = value;

            break;
          }
          case 0x0128: {
            resolutionUnit = value;

            break;
          }
          // No default
        }
      }

      if (!xResolution && !yResolution) {
        return null;
      }

      const resolvedX = xResolution ?? yResolution;
      const resolvedY = yResolution ?? xResolution;

      if (!resolvedX || !resolvedY) {
        return null;
      }

      let dpiX = resolvedX;
      let dpiY = resolvedY;

      if (resolutionUnit === 3) {
        dpiX *= 2.54;
        dpiY *= 2.54;
      }

      const roundedX = Math.round(dpiX);
      const roundedY = Math.round(dpiY);

      if (
        roundedX < MIN_BELIEVABLE_RESOLUTION_PPI ||
        roundedY < MIN_BELIEVABLE_RESOLUTION_PPI
      ) {
        return null;
      }

      return {
        XResolution: String(roundedX),
        YResolution: String(roundedY),
        ResolutionUnit: resolutionUnit === 3 ? "3" : "2",
      };
    }

    offset = segmentEnd;
  }

  return null;
};

const readFileHeaderBytes = async (file: File): Promise<Uint8Array | null> => {
  if (!file || typeof file.slice !== "function") {
    return null;
  }

  const headerBlob = file.slice(0, EXIF_HEADER_BYTES);

  return new Uint8Array(await headerBlob.arrayBuffer());
};

const readEmbeddedImageMetadata = async (
  file: File
): Promise<RasterUploadImageMetadata | undefined> => {
  const bytes = await readFileHeaderBytes(file);

  if (!bytes) {
    return undefined;
  }
  const format = detectLocalRasterImageFormat(file.type, bytes);

  if (format !== "jpeg") {
    return undefined;
  }

  const metadata = readJpegExifResolution(bytes);

  return metadata ?? undefined;
};

export const readRasterUploadMetadata = async (
  file: File
): Promise<RasterUploadMetadata> => {
  const [{ width, height }, imageMetadata] = await Promise.all([
    readPixelDimensions(file),
    readEmbeddedImageMetadata(file),
  ]);

  return {
    width,
    height,
    bytes: file.size,
    imageMetadata,
  };
};
